import { useEffect, useMemo, useState } from "react";
import { fetchRestaurants, fetchProfiles, fetchReviewAggregates } from "@/lib/api";
import RestaurantCard from "@/components/RestaurantCard";
import ProfileCard from "@/components/ProfileCard";

const CATEGORIES = ["All", "Restaurant", "Chef", "Foodie", "Bartender", "Food Truck", "Caterer", "Bar", "Brewery", "Bakery", "Coffee Shop"];
const CITIES = ["All Cities", "Houston", "Atlanta", "Dallas", "Los Angeles", "New Orleans", "New York", "San Diego", "Golden Triangle"];

export default function Directory() {
  const [restaurants, setRestaurants] = useState([]);
  const [profiles, setProfiles] = useState([]);
  const [aggregates, setAggregates] = useState({});
  const [active, setActive] = useState("All");
  const [city, setCity] = useState("All Cities");
  const [sort, setSort] = useState("featured");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      fetchRestaurants(),
      fetchProfiles().catch(() => []),
      fetchReviewAggregates().catch(() => ({})),
    ])
      .then(([r, p, agg]) => {
        setRestaurants(r);
        setProfiles(p);
        setAggregates(agg || {});
      })
      .finally(() => setLoading(false));
  }, []);

  const all = useMemo(() => {
    const withRating = (item, id) => {
      const agg = aggregates[id];
      return { ...item, community_rating: agg?.average || 0, review_count: agg?.count || 0 };
    };
    return [
      ...profiles.map((p) => withRating({ ...p, _profile: true, _category: p.type }, p.id)),
      ...restaurants.map((r) => withRating({ ...r, _profile: false, _category: r.category }, r.id)),
    ];
  }, [restaurants, profiles, aggregates]);

  const filtered = useMemo(() => {
    const list = all.filter(
      (item) =>
        (active === "All" || item._category === active) &&
        (city === "All Cities" || item.city === city)
    );
    if (sort === "top") {
      return list.sort(
        (a, b) =>
          (b.community_rating || 0) - (a.community_rating || 0) ||
          (b.review_count || 0) - (a.review_count || 0) ||
          (b.rating || 0) - (a.rating || 0)
      );
    }
    return list.sort(
      (a, b) =>
        Number(b.verified) - Number(a.verified) ||
        Number(b.pro_member || false) - Number(a.pro_member || false) ||
        (b.rating || 0) - (a.rating || 0)
    );
  }, [all, active, city, sort]);

  return (
    <div data-testid="directory-page" className="bg-sand-50">
      <section className="max-w-7xl mx-auto px-6 md:px-12 py-16 md:py-24">
        <div className="font-mono text-[11px] tracking-[0.3em] uppercase text-brand-500 mb-4">
          The Directory · 8 Markets
        </div>
        <h1 className="font-serif font-black text-5xl sm:text-6xl lg:text-7xl leading-[1.02] tracking-tight text-sand-900 max-w-4xl">
          Every kitchen,<br />bar & food truck<br />worth knowing.
        </h1>
        <p className="mt-6 max-w-2xl text-lg font-light text-sand-700 leading-relaxed">
          From Houston Creole to Brooklyn slice shops, our directory is a hand-curated
          map of America's most exciting food cities — Houston, Atlanta, Dallas,
          Los Angeles, New Orleans, New York, San Diego & the Golden Triangle of Southeast Texas.
        </p>

        <div className="mt-12">
          <div className="font-mono text-[10px] tracking-[0.25em] uppercase text-sand-700 mb-3">City</div>
          <div className="flex flex-wrap gap-2.5" data-testid="city-filters">
            {CITIES.map((c) => (
              <button
                key={c}
                data-testid={`city-filter-${c.toLowerCase().replace(/\s/g, "-")}`}
                onClick={() => setCity(c)}
                className={`px-5 py-2.5 font-mono text-[10px] tracking-[0.2em] uppercase border transition-all active:scale-95 ${
                  city === c
                    ? "bg-brand-500 text-sand-50 border-brand-500"
                    : "bg-transparent text-sand-900 border-sand-300 hover:border-brand-500"
                }`}
              >
                {c}
              </button>
            ))}
          </div>
        </div>

        <div className="mt-6">
          <div className="font-mono text-[10px] tracking-[0.25em] uppercase text-sand-700 mb-3">Category</div>
          <div className="flex flex-wrap gap-2.5" data-testid="category-filters">
            {CATEGORIES.map((c) => (
              <button
                key={c}
                data-testid={`filter-${c.toLowerCase().replace(/\s/g, "-")}`}
                onClick={() => setActive(c)}
                className={`px-5 py-2.5 font-mono text-[10px] tracking-[0.2em] uppercase border transition-all active:scale-95 ${
                  active === c
                    ? "bg-sand-900 text-sand-50 border-sand-900"
                    : "bg-transparent text-sand-900 border-sand-300 hover:border-sand-900"
                }`}
              >
                {c}
              </button>
            ))}
          </div>
        </div>

        <div className="mt-6">
          <div className="font-mono text-[10px] tracking-[0.25em] uppercase text-sand-700 mb-3">Sort By</div>
          <div className="flex flex-wrap gap-2.5" data-testid="sort-controls">
            {[
              { key: "featured", label: "Featured" },
              { key: "top", label: "Top Rated" },
            ].map((s) => (
              <button
                key={s.key}
                data-testid={`sort-${s.key}`}
                onClick={() => setSort(s.key)}
                className={`px-5 py-2.5 font-mono text-[10px] tracking-[0.2em] uppercase border transition-all active:scale-95 ${
                  sort === s.key
                    ? "bg-ochre-500 text-sand-900 border-ochre-500"
                    : "bg-transparent text-sand-900 border-sand-300 hover:border-ochre-500"
                }`}
              >
                {s.label}
              </button>
            ))}
          </div>
        </div>

        <div className="mt-6 font-mono text-xs text-sand-600" data-testid="result-count">
          {filtered.length} {filtered.length === 1 ? "listing" : "listings"}
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 md:px-12 pb-32">
        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-10">
            {[1, 2, 3, 4, 5, 6].map((i) => (
              <div key={i} className="aspect-[4/5] bg-sand-100 animate-pulse" />
            ))}
          </div>
        ) : filtered.length === 0 ? (
          <div className="py-24 text-center">
            <p className="font-serif italic text-2xl text-sand-700">No listings match those filters yet.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-10 gap-y-16">
            {filtered.map((item, i) =>
              item._profile ? (
                <ProfileCard key={item.id} p={item} index={i} />
              ) : (
                <RestaurantCard key={item.id} r={item} index={i} />
              )
            )}
          </div>
        )}
      </section>
    </div>
  );
}
